import { useMemo } from "react";

import { useCan, useRealTimeTypedQuery } from "components";
import { typedQueryClient } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES, CHECKLIST_TYPES } from "models";
import { AppointmentDetailsKeys } from "modules/AppointmentDetails/queryKeys";
import { ApiInstance } from "utils/Api";

import type { WebSocketMessageListener } from "components";
import type {
  CarCheck,
  ChecklistBrandGroup,
  ChecklistID,
  Pin,
  QuestionOption,
  QuestionResult,
  QuestionResultImage,
  QuestionResultVideo,
  Snooze,
  Tag,
  TagID,
  TyreReplacement
} from "models";

type ChecklistsTag = {
  checklist_id: ChecklistID;
  tag_id: TagID;
};

export interface OrderedGroupedCheck extends CarCheck {
  question_items: QuestionResult[];
  _groupedQuestionResults: Map<number, SortedQuestionResult[]>;
  _isLastCheck: boolean;
  _isTyreCheck: boolean;
  _order: number;
}

export interface SortedQuestionResult extends QuestionResult {
  pin_history: Pin[];
}

const sortQuestionResults = (questionResults?: QuestionResult[]): SortedQuestionResult[] => {
  return (
    questionResults
      ?.map(qr => ({ ...qr, pin_history: qr.pin_history?.toSorted((a, b) => b.id - a.id) ?? [] }))
      .sort((a, b) => {
        if (a.group_order === b.group_order) {
          if (a.question_order < b.question_order) return -1;
          if (a.question_order > b.question_order) return 1;
          if (a.id < b.id) return -1;
          if (a.id > b.id) return 1;
          return 0;
        }
        return a.group_order < b.group_order ? -1 : 1;
      }) ?? []
  );
};

const groupQuestionResults = (questionResults?: SortedQuestionResult[]) => {
  const grouped = new Map<number, SortedQuestionResult[]>();

  questionResults?.forEach(qr => {
    const group = grouped.get(qr.group_order);
    if (group) group.push(qr);
    else grouped.set(qr.group_order, [qr]);
  });

  return grouped;
};

const orderAndGroupChecks = (checks: CarCheck[]): OrderedGroupedCheck[] => {
  const extraPartChecks: CarCheck[] = [];
  const otherChecks: CarCheck[] = [];

  checks.forEach(check => (check.checklist?.checklist_type === CHECKLIST_TYPES.ExtraParts ? extraPartChecks.push(check) : otherChecks.push(check)));

  extraPartChecks.sort((a, b) => b.id - a.id);
  otherChecks.sort((a, b) => a.id - b.id);

  return (extraPartChecks.length > 0 ? [extraPartChecks[0], ...otherChecks] : otherChecks)
    .map(check => ({ ...check, question_items: sortQuestionResults(check.question_items) }))
    .map((check, index) => ({
      ...check,
      _groupedQuestionResults: groupQuestionResults(check.question_items),
      _isLastCheck: index === otherChecks.length - 1,
      _isTyreCheck: check.question_items.some(q => q.tyre_id),
      _order: index + 1
    }));
};

export const useChecksListeners = (appointment_id?: number) =>
  useMemo(() => {
    if (!appointment_id) return [];

    const checkListeners: WebSocketMessageListener<CarCheck>[] = [
      {
        model: "CarCheck",
        action: "upsert",
        filter: { appointment_id },
        callback: ({ data: check }) => {
          const checks = typedQueryClient.getQueryData(AppointmentDetailsKeys.checks) ?? [];

          const checkIdx = checks.findIndex(c => c.id === check.id);
          typedQueryClient.setQueryData(AppointmentDetailsKeys.checks, checkIdx > -1 ? checks.with(checkIdx, { ...checks[checkIdx], ...check }) : [...checks, check]);
        }
      }
    ];

    const questionResultListeners: WebSocketMessageListener<QuestionResult>[] = [
      {
        model: "QuestionResult",
        action: "upsert",
        filter: { appointment_id },
        callback: ({ data: question }) => {
          const checks = typedQueryClient.getQueryData(AppointmentDetailsKeys.checks);
          if (!checks?.length) return;

          const checkIdx = checks.findIndex(check => check.id === question.car_check_id);
          if (checkIdx < 0) return;

          let questions = [...(checks[checkIdx].question_items ?? [])];
          const questionIdx = questions.findIndex(q => q.id === question.id);
          questions = questionIdx < 0 ? [...questions, question] : questions.with(questionIdx, { ...questions[questionIdx], ...question });

          typedQueryClient.setQueryData(AppointmentDetailsKeys.checks, checks.with(checkIdx, { ...checks[checkIdx], question_items: questions }));

          const appointment = typedQueryClient.getQueryData(AppointmentDetailsKeys.view);
          if (!appointment?.interventions?.length) return;

          const interventions = appointment.interventions;
          const interventionIdx = interventions.findIndex(i => i.question_result_id === question.id);
          if (interventionIdx < 0) return;

          typedQueryClient.setQueryData(AppointmentDetailsKeys.view, {
            ...appointment,
            interventions: interventions.with(interventionIdx, { ...interventions[interventionIdx], question_result_status: question.status })
          });
        }
      }
    ];

    const questionResultImageListeners: WebSocketMessageListener<QuestionResultImage>[] = [
      {
        model: "QuestionResultImage",
        action: "upsert",
        callback: ({ data: questionImage }) => {
          const checks = typedQueryClient.getQueryData(AppointmentDetailsKeys.checks);
          if (!checks?.length) return;

          const checkIdx = checks.findIndex(check => check.question_items?.some(q => q.id === questionImage.question_result_id));
          if (checkIdx < 0 || !checks[checkIdx].question_items?.length) return;

          const questionIdx = checks[checkIdx].question_items.findIndex(q => q.id === questionImage.question_result_id);
          if (questionIdx < 0) return;

          let images = [...(checks[checkIdx].question_items[questionIdx].images ?? [])];
          const imageIdx = images.findIndex(image => image.id === questionImage.id);
          images = imageIdx < 0 ? [...images, questionImage] : images.with(imageIdx, { ...images[imageIdx], ...questionImage });

          typedQueryClient.setQueryData(
            AppointmentDetailsKeys.checks,
            checks.with(checkIdx, {
              ...checks[checkIdx],
              question_items: checks[checkIdx].question_items.with(questionIdx, { ...checks[checkIdx].question_items[questionIdx], images })
            })
          );
        }
      }
    ];

    const questionResultVideoListeners: WebSocketMessageListener<QuestionResultVideo>[] = [
      {
        model: "QuestionResultVideo",
        action: "upsert",
        callback: ({ data: questionVideo }) => {
          const checks = typedQueryClient.getQueryData(AppointmentDetailsKeys.checks);
          if (!checks?.length) return;

          const checkIdx = checks.findIndex(check => check.question_items?.some(q => q.id === questionVideo.question_result_id));
          if (checkIdx < 0 || !checks[checkIdx].question_items?.length) return;

          const questionIdx = checks[checkIdx].question_items.findIndex(q => q.id === questionVideo.question_result_id);
          if (questionIdx < 0) return;

          let videos = [...(checks[checkIdx].question_items[questionIdx].videos ?? [])];
          const videoIdx = videos.findIndex(video => video.id === questionVideo.id);
          videos = videoIdx < 0 ? [...videos, questionVideo] : videos.with(videoIdx, { ...videos[videoIdx], ...questionVideo });

          typedQueryClient.setQueryData(
            AppointmentDetailsKeys.checks,
            checks.with(checkIdx, {
              ...checks[checkIdx],
              question_items: checks[checkIdx].question_items.with(questionIdx, { ...checks[checkIdx].question_items[questionIdx], videos })
            })
          );
        }
      }
    ];

    const snoozeListeners: WebSocketMessageListener<Snooze>[] = [
      {
        model: "Snooze",
        action: "upsert",
        filter: { appointment_id },
        callback: ({ data: snooze }) => {
          const checks = typedQueryClient.getQueryData(AppointmentDetailsKeys.checks);
          if (!checks?.length) return;

          const checkIdx = checks.findIndex(check => check.question_items?.some(qr => qr.id === snooze.question_result_id));
          if (checkIdx < 0 || !checks[checkIdx].question_items?.length) return;

          const questionIdx = checks[checkIdx].question_items.findIndex(qr => qr.id === snooze.question_result_id);
          if (questionIdx < 0) return;

          let snoozes = [...(checks[checkIdx].question_items[questionIdx].snooze_history ?? [])];
          const snoozeIdx = snoozes.findIndex(s => s.id === snooze.id);
          snoozes = snoozeIdx < 0 ? [snooze, ...snoozes] : snoozes.with(snoozeIdx, { ...snoozes[snoozeIdx], ...snooze });

          typedQueryClient.setQueryData(
            AppointmentDetailsKeys.checks,
            checks.with(checkIdx, {
              ...checks[checkIdx],
              question_items: checks[checkIdx].question_items.with(questionIdx, {
                ...checks[checkIdx].question_items[questionIdx],
                snooze_history: snoozes,
                snoozed: true
              })
            })
          );
        }
      },
      {
        model: "Snooze",
        action: "delete",
        filter: { appointment_id },
        callback: ({ data: snooze }) => {
          const checks = typedQueryClient.getQueryData(AppointmentDetailsKeys.checks);
          if (!checks?.length) return;

          const checkIdx = checks.findIndex(check => check.question_items?.some(item => item.id === snooze.question_result_id));
          if (checkIdx < 0 || !checks[checkIdx].question_items?.length) return;

          const questions = checks[checkIdx].question_items;
          const questionIdx = questions.findIndex(qi => qi.id === snooze.question_result_id);
          if (questionIdx < 0 || !questions[questionIdx].snooze_history?.length) return;

          const snoozeIdx = questions[questionIdx].snooze_history.findIndex(s => s.id === snooze.id);
          if (snoozeIdx < 0) return;

          const snoozes = questions[questionIdx].snooze_history.toSpliced(snoozeIdx, 1);
          typedQueryClient.setQueryData(
            AppointmentDetailsKeys.checks,
            checks.with(checkIdx, {
              ...checks[checkIdx],
              question_items: questions.with(questionIdx, { ...questions[questionIdx], snooze_history: snoozes, snoozed: snoozes.length > 0 })
            })
          );
        }
      }
    ];

    const tyreReplacementListeners: WebSocketMessageListener<TyreReplacement>[] = [
      {
        model: "TyreReplacement",
        action: "upsert",
        filter: { appointment_id },
        callback: ({ data: replacement }) => {
          const checks = typedQueryClient.getQueryData(AppointmentDetailsKeys.checks);
          if (!checks?.length) return;

          const checkIdx = checks.findIndex(c => c.question_items?.some(qi => qi.id === replacement.question_result_id));
          if (checkIdx < 0 || !checks[checkIdx].question_items?.length) return;

          const questionIdx = checks[checkIdx].question_items.findIndex(q => q.id === replacement.question_result_id);
          if (questionIdx < 0) return;

          let replacements = [...(checks[checkIdx].question_items[questionIdx].tyre_replacements ?? [])];
          const replacementIdx = replacements.findIndex(t => t.tyre_replacement_id === replacement.tyre_replacement_id);
          if (replacementIdx < 0) replacements = [...replacements, replacement];
          else if (replacement.customer_answer)
            replacements = replacements.map(r => (r.tyre_replacement_id === replacement.tyre_replacement_id ? replacement : { ...r, customer_answer: false }));
          else replacements = replacements.with(replacementIdx, { ...replacements[replacementIdx], ...replacement });

          typedQueryClient.setQueryData(
            AppointmentDetailsKeys.checks,
            checks.with(checkIdx, {
              ...checks[checkIdx],
              question_items: checks[checkIdx].question_items.with(questionIdx, { ...checks[checkIdx].question_items[questionIdx], tyre_replacements: replacements })
            })
          );
        }
      },
      {
        model: "TyreReplacement",
        action: "delete",
        filter: { appointment_id },
        callback: ({ data: replacement }) => {
          const checks = typedQueryClient.getQueryData(AppointmentDetailsKeys.checks);
          if (!checks?.length) return;

          const checkIdx = checks.findIndex(c => c.question_items?.some(qi => qi.id === replacement.question_result_id));
          if (checkIdx < 0 || !checks[checkIdx].question_items?.length) return;

          const questions = checks[checkIdx].question_items;
          const questionIdx = questions.findIndex(q => q.id === replacement.question_result_id);
          if (questionIdx < 0 || !questions[questionIdx].tyre_replacements?.length) return;

          const replacementIdx = questions[questionIdx].tyre_replacements.findIndex(t => t.tyre_replacement_id === replacement.tyre_replacement_id);
          if (replacementIdx < 0) return;

          const replacements = questions[questionIdx].tyre_replacements.toSpliced(replacementIdx, 1);
          typedQueryClient.setQueryData(
            AppointmentDetailsKeys.checks,
            checks.with(checkIdx, { ...checks[checkIdx], question_items: questions.with(questionIdx, { ...questions[questionIdx], tyre_replacements: replacements }) })
          );
        }
      }
    ];

    const questionTagListeners: WebSocketMessageListener<Tag>[] = [
      {
        model: "Tag",
        action: "delete",
        callback: ({ id: questionId, data: tag }) => {
          const checks = typedQueryClient.getQueryData(AppointmentDetailsKeys.checks);
          if (!checks?.length) return;

          const checkIdx = checks.findIndex(check => check.question_items?.some(qr => qr.question_id === questionId));
          if (checkIdx === -1) return;

          const check = checks[checkIdx];
          if (!check.question_items?.length) return;

          const questions = check.question_items;
          const questionIdx = questions.findIndex(qr => qr.question_id === questionId);
          if (questionIdx === -1) return;

          const question = questions[questionIdx];
          if (!question.question_tags?.length) return;

          const tagIdx = question.question_tags.findIndex(questionTag => questionTag.id === tag.id);
          if (tagIdx === -1) return;

          const questionTags = question.question_tags.toSpliced(tagIdx, 1);
          typedQueryClient.setQueryData(
            AppointmentDetailsKeys.checks,
            checks.with(checkIdx, { ...check, question_items: questions.with(questionIdx, { ...question, question_tags: questionTags }) })
          );
        }
      }
    ];

    const questionOptionListeners: WebSocketMessageListener<QuestionOption>[] = [
      {
        model: "QuestionOption",
        action: "delete",
        callback: ({ id: questionId, data: option }) => {
          const checks = typedQueryClient.getQueryData(AppointmentDetailsKeys.checks);
          if (!checks?.length) return;

          const checkIdx = checks.findIndex(check => check.question_items?.some(qr => qr.question_id === questionId));
          if (checkIdx === -1) return;

          const check = checks[checkIdx];
          if (!check.question_items?.length) return;

          const questions = check.question_items;
          const questionIdx = questions.findIndex(qr => qr.question_id === questionId);
          if (questionIdx === -1) return;

          const question = questions[questionIdx];
          if (!question.question_options?.length) return;

          const optionIdx = question.question_options.findIndex(questionOption => questionOption.id === option.id);
          if (optionIdx === -1) return;

          const questionOptions = question.question_options.toSpliced(optionIdx, 1);
          typedQueryClient.setQueryData(
            AppointmentDetailsKeys.checks,
            checks.with(checkIdx, { ...check, question_items: questions.with(questionIdx, { ...question, question_options: questionOptions }) })
          );
        }
      }
    ];

    const checklistBrandGroupListeners: WebSocketMessageListener<ChecklistBrandGroup>[] = [
      {
        model: "ChecklistBrandGroup",
        action: "delete",
        callback: ({ data: { checklist_id, brand_group_id } }) => {
          const checks = typedQueryClient.getQueryData(AppointmentDetailsKeys.checks);
          if (!checks?.length) return;

          const checkIdx = checks.findIndex(check => check.checklist?.id === checklist_id);
          if (checkIdx === -1) return;

          const check = checks[checkIdx];
          if (!check.checklist?.brand_groups?.length) return;

          const brandGroups = check.checklist.brand_groups;
          const brandGroupIdx = brandGroups.findIndex(brandGroup => brandGroup.id === brand_group_id);
          if (brandGroupIdx === -1) return;

          typedQueryClient.setQueryData(
            AppointmentDetailsKeys.checks,
            checks.with(checkIdx, { ...check, checklist: { ...check.checklist, brand_groups: brandGroups.toSpliced(brandGroupIdx, 1) } })
          );
        }
      }
    ];

    const checklistTagListeners: WebSocketMessageListener<ChecklistsTag>[] = [
      {
        model: "ChecklistsTag",
        action: "delete",
        callback: ({ data: { checklist_id, tag_id } }) => {
          const checks = typedQueryClient.getQueryData(AppointmentDetailsKeys.checks);
          if (!checks?.length) return;

          const checkIdx = checks.findIndex(check => check.checklist?.id === checklist_id);
          if (checkIdx === -1) return;

          const check = checks[checkIdx];
          if (!check.checklist?.tags?.length) return;

          const tags = check.checklist.tags;
          const tagIdx = tags.findIndex(tag => tag.id === tag_id);
          if (tagIdx === -1) return;

          typedQueryClient.setQueryData(
            AppointmentDetailsKeys.checks,
            checks.with(checkIdx, { ...check, checklist: { ...check.checklist, tags: tags.toSpliced(tagIdx, 1) } })
          );
        }
      }
    ];

    return [
      ...checkListeners,
      ...questionResultListeners,
      ...questionResultImageListeners,
      ...questionResultVideoListeners,
      ...snoozeListeners,
      ...tyreReplacementListeners,
      ...questionTagListeners,
      ...questionOptionListeners,
      ...checklistBrandGroupListeners,
      ...checklistTagListeners
    ];
  }, [appointment_id]);

export const useRealTimeOrderedChecks = (appointment_id?: number) => {
  const canListChecks = useCan(ACL_ACTIVITIES.List, ACL_RESOURCES.CarChecks);

  const { data } = useRealTimeTypedQuery({
    enabled: canListChecks && appointment_id !== undefined,
    queryKey: AppointmentDetailsKeys.checks,
    listeners: useChecksListeners(appointment_id),
    queryFn: async ({ baseUrl, endpoint, params }) => (await ApiInstance.post(endpoint, { appointment_id, ...params }, baseUrl)).data.car_checks,
    select: orderAndGroupChecks
  });

  return { checks: data };
};
